import classNames from "classnames";
import {
	KeyBoardListNavigationContext,
	useKeyboardListNavigation,
	useKeyboardListNavigationItem,
	useResizeObserver
} from "client/Hooks";
import {
	EMPTY_ARRAY,
	generateTrackingId,
	isNil,
	setTrackingId
} from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import {
	Fragment,
	ReactElement,
	RefObject,
	useCallback,
	useMemo,
	useRef,
	useState,
	KeyboardEvent
} from "react";
import { ActionListItem } from "../ActionList/ActionListItem";
import { DropOverActionList } from "../DropOverActionList/DropOverActionList";
import { Icon } from "../Icon/Icon";
import styles from "./Breadcrumb.module.less";
import { Key } from "client/Misc/commonTypes";
import { ButtonBase } from "../ButtonBase/ButtonBase";

export type BreadcrumbSegment = {
	name: string;
};

export type BreadcrumbProps<TSegment> = {
	segments: readonly TSegment[];
	onNavigate(segment: TSegment): void;
	trackingId: string;
	getSegmentTrackingId: (segment: TSegment) => string;
};

export const Breadcrumb = <TSegment extends BreadcrumbSegment>(
	props: BreadcrumbProps<TSegment>
): ReactElement => {
	const {
		segments,
		onNavigate,
		trackingId,
		getSegmentTrackingId
	} = props;

	const listRef: RefObject<HTMLUListElement | null> = useRef<HTMLUListElement>(null);
	const { width: containerWidth, wasInitialized } = useResizeObserver(listRef);

	const maxAmountOfSegmentsToShow: number = getBreadcrumbMaxAmountOfTextSegmentsForContainerWidth(containerWidth);

	const [
		leading,
		collapsed,
		trailing
	] = useMemo(
		(): [
			leading: readonly TSegment[],
			collapsed: readonly TSegment[],
			trailing: readonly TSegment[]
		] => {
			let leading: readonly TSegment[] = segments;
			let collapsed: readonly TSegment[] = EMPTY_ARRAY;
			let trailing: readonly TSegment[] = EMPTY_ARRAY;

			if (segments.length > maxAmountOfSegmentsToShow) {
				const numberOfLeadingSegments: number = Math.floor(maxAmountOfSegmentsToShow / 2);
				const numberOfTrailingSegments: number = maxAmountOfSegmentsToShow - (numberOfLeadingSegments + 1);

				leading = segments.slice(0, numberOfLeadingSegments);
				collapsed = segments.slice(numberOfLeadingSegments, -numberOfTrailingSegments);
				trailing = segments.slice(-numberOfTrailingSegments);
			}

			return [
				leading,
				collapsed,
				trailing
			];
		},
		[maxAmountOfSegmentsToShow, segments]
	);

	const lastSegment: TSegment | undefined = segments[segments.length - 1];

	const createItemNavigateHandler = (segment: TSegment): (() => void) | undefined => {
		return segment !== lastSegment
			? () => onNavigate(segment)
			: undefined;
	};

	const wrappedGetSegmentTrackingId = (segment: TSegment): string => {
		return generateTrackingId(
			trackingId,
			`|${getSegmentTrackingId(segment)}`
		);
	};

	const amountOfTextSegments: number = leading.length + trailing.length;

	return (
		<ul
			{...setTrackingId(trackingId)}
			className={styles.root}
			ref={listRef}
			style={{
				visibility: wasInitialized ? undefined : "hidden",
				["--breadcrumb-amount-of-text-segments"]: amountOfTextSegments
			}}
		>
			{segments.length === 0 && (
				<Item
					segment={{ name: "placeholder" }}
					onNavigate={undefined}
					trackingId={generateTrackingId(trackingId, "__dummy")}
					isHidden
				/>
			)}

			{leading.map((x, i) => (
				<Fragment key={i}>
					{i > 0 && (
						<Separator />
					)}

					<Item
						segment={x}
						onNavigate={createItemNavigateHandler(x)}
						trackingId={wrappedGetSegmentTrackingId(x)}
					/>
				</Fragment>
			))}

			{collapsed.length > 0 && (
				<>
					<Separator />
					<More
						segments={collapsed}
						onNavigate={onNavigate}
						trackingId={generateTrackingId(trackingId, "__more")}
						getSegmentTrackingId={wrappedGetSegmentTrackingId}
					/>
				</>
			)}

			{trailing.map((x, i) => (
				<Fragment key={i}>
					<Separator />
					<Item
						segment={x}
						onNavigate={createItemNavigateHandler(x)}
						trackingId={wrappedGetSegmentTrackingId(x)}
					/>
				</Fragment>
			))}
		</ul>
	);
};
Breadcrumb.displayName = "Breadcrumb";

type ItemProps<TSegment extends BreadcrumbSegment> = {
	segment: TSegment;
	onNavigate: (() => void) | undefined;
	trackingId: string;
	isHidden?: boolean;
};

const Item = <TSegment extends BreadcrumbSegment>(
	props: ItemProps<TSegment>
): ReactElement => {
	const {
		segment,
		onNavigate,
		trackingId,
		isHidden
	} = props;

	return (
		<li className={
			classNames({
				[styles.listItem]: true,
				[styles.listItem_isHidden]: isHidden
			})
		}>
			{isNil(onNavigate)
				? (
					<span
						{...setTrackingId(trackingId)}
						className={styles.item}
						title={segment.name}
					>
						{segment.name}
					</span>
				)
				: (
					<ButtonBase
						trackingId={trackingId}
						className={classNames(
							styles.item,
							styles.item_interactable
						)}
						onClick={onNavigate}
						title={segment.name}
					>
						{segment.name}
					</ButtonBase>
				)
			}
		</li>
	);
};
Item.displayName = "Item";

type MoreProps<TSegment extends BreadcrumbSegment> = {
	segments: readonly TSegment[];
	onNavigate: (segment: TSegment) => void;
	trackingId: string;
	getSegmentTrackingId: (segment: TSegment) => string;
};

const More = <TSegment extends BreadcrumbSegment>(
	props: MoreProps<TSegment>
): ReactElement => {
	const {
		segments,
		onNavigate,
		trackingId,
		getSegmentTrackingId
	} = props;

	const [isOpen, setIsOpen] = useState<boolean>(false);
	const ref: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);

	const focusPreviewHandler = useCallback(
		(): void => {
			ref.current
				?.focus();
		},
		[]
	);

	const cancelHandler = useCallback(
		(): void => {
			setIsOpen(false);

			focusPreviewHandler();
		},
		[focusPreviewHandler]
	);

	const itemClickHandler = useCallback(
		(segment: TSegment): void => {
			setIsOpen(false);
			focusPreviewHandler();
			onNavigate(segment);
		},
		[focusPreviewHandler, onNavigate]
	);

	const {
		contextData,
		focusItemAtIndexHandler
	} = useKeyboardListNavigation({
		onCancel: cancelHandler,
		shouldCancelDirectlyFromItems: true
	});

	const moreButtonKeyDownHandler = useCallback(
		(e: KeyboardEvent): void => {
			switch (e.key) {
				case Key.ArrowDown:
				case Key.Enter:
				case Key.Space:
					e.preventDefault();
					setIsOpen(true);

					// Ensure the dropdown is mounted before focusing an item
					setTimeout(
						(): void => {
							focusItemAtIndexHandler(0);
						},
						1
					);
					return;
				case Key.Escape:
				case Key.Tab:
					setIsOpen(false);
					return;
				default:
					return;
			}
		},
		[focusItemAtIndexHandler]
	);

	return (
		<li className={styles.listItem}>
			<ButtonBase
				trackingId={trackingId}
				className={styles.more}
				onClick={() => setIsOpen(!isOpen)}
				ref={ref}
				onKeyDown={moreButtonKeyDownHandler}
			>
				<Icon value="ellipsis" size="medium" />
			</ButtonBase>
			{(isOpen && ref.current) && (
				<DropOverActionList
					target={ref.current}
					onClose={() => setIsOpen(false)}
					isHeightConstrainted
					trackingId={generateTrackingId(trackingId, "__dropover")}
					menuTitle={localize("_Folders")}
				>
					<KeyBoardListNavigationContext.Provider value={contextData}>
						{segments.map((item, index) => (
							<MoreMenuItem
								key={index}
								text={item.name}
								onClick={() => itemClickHandler(item)}
								trackingId={getSegmentTrackingId(item)}
								index={index}
								onFocusPreview={focusPreviewHandler}
								onCancel={cancelHandler}
							/>
						))}
					</KeyBoardListNavigationContext.Provider>
				</DropOverActionList>
			)}
		</li>
	);
};
More.displayName = "More";

type MoreMenuItemProps = {
	onFocusPreview: () => void;
	onClick: () => void;
	onCancel: () => void;
	trackingId: string;
	index: number;
	text: string;
};

const MoreMenuItem = (props: MoreMenuItemProps): ReactElement => {
	const {
		trackingId,
		text,
		onClick,
		onFocusPreview,
		onCancel,
		index
	} = props;

	const keyboardExecuteHandler = useCallback(
		(key: Key): void => {
			if (key === Key.Tab) {
				onCancel();
				return;
			}

			onClick();
			onFocusPreview();
		},
		[
			onCancel,
			onClick,
			onFocusPreview
		]
	);

	const {
		onKeyDown,
		ref
	} = useKeyboardListNavigationItem({
		itemIndex: index,
		onExecute: keyboardExecuteHandler,
		executeKey: [
			Key.Enter,
			Key.Space,
			Key.Tab
		]
	});

	return (
		<ActionListItem
			ref={ref}
			onKeyDown={onKeyDown}
			text={text}
			onClick={onClick}
			trackingId={trackingId}
			tabIndex={-1}
		/>
	);
};
MoreMenuItem.displayName = "MoreMenuItem";

const Separator = (): ReactElement => {
	return (
		<li className={styles.listItem}>
			<div className={styles.separator}>
				<Icon value="chevron-right" size="medium" />
			</div>
		</li>
	);
};
Separator.displayName = "Separator";

export function getBreadcrumbMaxAmountOfTextSegmentsForContainerWidth(width: number): number {
	if (width < 587) {
		return 3;
	}

	return 5;
}
